import React, { useState } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { useTranslation } from "react-i18next";
import { useMutation } from "@tanstack/react-query";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Loader2, CheckCircle2, Mail } from "lucide-react";
import { waitlistService, WaitlistEntry } from "@/services/waitlistService";
import { cn } from "@/lib/utils";

// 表单验证 schema
const subscriptionSchema = z.object({
  email: z.string().email("请输入有效的邮箱地址"),
  name: z.string().optional(),
});

type SubscriptionFormData = z.infer<typeof subscriptionSchema>;

export interface WaitlistSubscriptionFormProps {
  campaignSlug: string;
  className?: string;
  showNameField?: boolean;
  onSuccess?: (entry: WaitlistEntry) => void;
  onError?: (error: Error) => void;
  successMessage?: string;
  buttonText?: string;
  placeholder?: string;
}

export function WaitlistSubscriptionForm({
  campaignSlug,
  className,
  showNameField = false,
  onSuccess,
  onError,
  successMessage,
  buttonText,
  placeholder,
}: WaitlistSubscriptionFormProps) {
  const { t } = useTranslation("waitlist");
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [requiresConfirmation, setRequiresConfirmation] = useState(false);

  const {
    register,
    handleSubmit,
    formState: { errors },
    reset,
  } = useForm<SubscriptionFormData>({
    resolver: zodResolver(subscriptionSchema),
  });

  const subscribeMutation = useMutation({
    mutationFn: async (data: SubscriptionFormData) => {
      // 获取 UTM 参数
      const urlParams = new URLSearchParams(window.location.search);

      return waitlistService.subscribe({
        campaign_slug: campaignSlug,
        email: data.email,
        name: data.name,
        utm_source: urlParams.get("utm_source") || undefined,
        utm_medium: urlParams.get("utm_medium") || undefined,
        utm_campaign: urlParams.get("utm_campaign") || undefined,
      });
    },
    onSuccess: (response) => {
      console.log("Subscribe success response:", response);
      setIsSubmitted(true);
      setRequiresConfirmation(response?.requires_confirmation ?? false);
      reset();
      if (response?.entry) {
        onSuccess?.(response.entry);
      }
    },
    onError: (error: Error) => {
      onError?.(error);
    },
  });

  const onSubmit = (data: SubscriptionFormData) => {
    subscribeMutation.mutate(data);
  };

  // 成功状态
  if (isSubmitted) {
    return (
      <div
        className={cn("flex items-center justify-center gap-3 py-2", className)}
      >
        <CheckCircle2 className="h-5 w-5 text-green-500 shrink-0" />
        <div className="text-sm">
          <span className="font-medium">
            {successMessage || t("subscribeSuccess", "订阅成功！")}
          </span>
          {requiresConfirmation && (
            <span className="text-muted-foreground ml-1">
              {t("confirmationSentShort", "请查收确认邮件")}
            </span>
          )}
        </div>
      </div>
    );
  }

  return (
    <form
      onSubmit={handleSubmit(onSubmit)}
      className={cn("space-y-4", className)}
    >
      {subscribeMutation.isError && (
        <Alert variant="destructive">
          <AlertDescription>
            {subscribeMutation.error?.message ||
              t("subscribeError", "订阅失败，请稍后重试")}
          </AlertDescription>
        </Alert>
      )}

      {showNameField && (
        <div className="space-y-2">
          <Label htmlFor="name">{t("name", "姓名")}</Label>
          <Input
            id="name"
            type="text"
            placeholder={t("namePlaceholder", "您的姓名（可选）")}
            {...register("name")}
            disabled={subscribeMutation.isPending}
          />
        </div>
      )}

      <div className="space-y-2">
        <Label htmlFor="email" className="sr-only">
          {t("email", "邮箱")}
        </Label>
        <div className="flex gap-2">
          <div className="relative flex-1">
            <Mail className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
            <Input
              id="email"
              type="email"
              placeholder={
                placeholder || t("emailPlaceholder", "请输入您的邮箱")
              }
              className="pl-10"
              {...register("email")}
              disabled={subscribeMutation.isPending}
              aria-invalid={errors.email ? "true" : "false"}
              aria-describedby={errors.email ? "email-error" : undefined}
            />
          </div>
          <Button type="submit" disabled={subscribeMutation.isPending}>
            {subscribeMutation.isPending ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                {t("subscribing", "订阅中...")}
              </>
            ) : (
              buttonText || t("subscribe", "订阅")
            )}
          </Button>
        </div>
        {errors.email && (
          <p id="email-error" className="text-sm text-destructive">
            {errors.email.message}
          </p>
        )}
      </div>
    </form>
  );
}

export default WaitlistSubscriptionForm;
